import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components/macro';
import { ITopic } from 'config/topics';
import { useViewport } from 'hooks/useViewport';
import { theme } from 'theme';
import { msTransition } from 'helpers/msTransition';

interface IProps {
  onClick: (topicId: number) => void;
  active?: boolean;
  topic: ITopic;
  onMouseOver: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
}

const StyledNavigationItem = styled.button<{ showLabel: boolean }>`
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${({ theme, showLabel }) => showLabel ? theme.spacing[1] : `0 ${theme.spacing[1]}`};
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;

  ${({ theme }) => `
    @media(min-width: ${theme.breakpoints.lg}px) {
      width: ${theme.spacing[8]};
    }

    @media(min-width: ${theme.breakpoints.xl}px) {
      width: 96px;
    }

    @media(min-width: ${theme.breakpoints.xxl}px) {
      width: 128px;
    }
  `};
`;

const StyledNavigationIcon = styled.div<Pick<IProps, 'active'>>`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme, active }) => active ? theme.colors.black : theme.colors.green};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  color: ${({ theme, active }) => active ? theme.colors.white : theme.colors.black};
  transition: ${({ theme }) => msTransition(theme.transition.slow)};

  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.black};
  }
`;

const StyledNavigationLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography['-1']};
  font-weight: 500;
`;

const NavigationItem: React.FC<IProps> = ({ topic, onClick, active, onMouseOver, onMouseLeave }) => {
  const { icon: NavIcon, label, id } = topic;
  const { width } = useViewport();
  const isDesktop = useMemo(() => width >= theme.breakpoints.xl, [width]);

  const handleOnClick = useCallback(() => {
    onClick(id);
  }, [id, onClick]);

  return (
    <StyledNavigationItem
      onClick={handleOnClick}
      showLabel={isDesktop}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <StyledNavigationIcon active={active}>
        <NavIcon width={32} height={32} />
      </StyledNavigationIcon>
      {isDesktop && (
        <StyledNavigationLabel>
          {label}
        </StyledNavigationLabel>
      )}
    </StyledNavigationItem>
  );
};

export { NavigationItem };
